export default class AnimationEngine {
  constructor() {
    this.observer = null;
    this.mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.prefersReducedMotion = this.mediaQuery.matches;
    this.handleMotionChange = this.handleMotionChange.bind(this);
  }

  init() {
    if (typeof this.mediaQuery.addEventListener === 'function') {
      this.mediaQuery.addEventListener('change', this.handleMotionChange);
    }

    document.documentElement.classList.toggle('reduce-motion', this.prefersReducedMotion);
    this.initScrollAnimations();
    this.initParallax();
  }

  initScrollAnimations() {
    const elements = document.querySelectorAll('[data-animate]');

    if (this.prefersReducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('animate-complete'));
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        this.animateIn(entry.target);
        this.observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

    elements.forEach((element) => {
      element.classList.add('animate-pending');
      this.observer.observe(element);
    });
  }

  animateIn(element) {
    if (!element) {
      return;
    }

    if (this.prefersReducedMotion) {
      element.classList.remove('animate-pending');
      element.classList.add('animate-complete');
      return;
    }

    const animation = element.dataset.animate || 'fade-up';
    const parsedDelay = Number.parseInt(element.dataset.animateDelay || '0', 10);
    const delay = Number.isNaN(parsedDelay) ? 0 : Math.min(parsedDelay, 1000);

    window.setTimeout(() => {
      element.classList.remove('animate-pending');
      element.classList.add('animate-in', `animate-${animation}`);
      element.addEventListener('animationend', () => {
        element.classList.remove('animate-in', `animate-${animation}`);
        element.classList.add('animate-complete');
      }, { once: true });
    }, delay);
  }

  initParallax() {
    const element = document.querySelector('[data-parallax]');

    if (!element || this.prefersReducedMotion) {
      return;
    }

    let ticking = false;
    const update = () => {
      const offset = Math.min(window.scrollY * 0.08, 42);
      element.style.transform = `translate3d(0, ${offset}px, 0)`;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  }

  handleMotionChange(event) {
    this.prefersReducedMotion = event.matches;
    document.documentElement.classList.toggle('reduce-motion', event.matches);

    if (event.matches) {
      document.querySelectorAll('.animate-pending').forEach((element) => {
        element.classList.remove('animate-pending');
        element.classList.add('animate-complete');
      });
    }
  }

  destroy() {
    this.observer?.disconnect();

    if (typeof this.mediaQuery.removeEventListener === 'function') {
      this.mediaQuery.removeEventListener('change', this.handleMotionChange);
    }
  }
}
